import { vrocManager, vrocFactory, VROCComponentFactory } from '../../_VROC/VROCComponentFactory.js';
import { ServiceManager } from './ServiceManager.js';

export class GuestsManager {
	constructor(invoiceId) {
		this.invoiceId = invoiceId;
		this.activeComponent = null; // Активный компонент (если есть)
		this._isRefreshing = false; // Флаг защиты от повторных обновлений
		this._registerComponent();
	}

	/**
	 * Обновляет компоненты сервисов после изменений в гостях
	 */
	async _refreshServiceComponents() {
		try {
			console.log('[GuestsManager] _refreshServiceComponents called');

			// Сначала очищаем неактивные компоненты ServiceManager
			ServiceManager.cleanupInactiveComponents();

			// Проверяем, есть ли активные компоненты сервисов
			if (ServiceManager.hasActiveComponents()) {
				console.log('[GuestsManager] Found active service components, refreshing...');
				await ServiceManager.refreshAllActiveComponents();
				console.log('[GuestsManager] Service components refresh completed');
			} else {
				console.log('[GuestsManager] No active service components to refresh');
			}
		} catch (error) {
			console.error('[GuestsManager] Error refreshing service components:', error);
		}
	}

	/**
	 * Обновляет компонент заказа после изменений в гостях
	 */
	async _refreshOrderComponent() {
		try {
			console.log('[GuestsManager] _refreshOrderComponent called');

			// Проверяем, есть ли активный компонент заказа на странице
			if (window.order && typeof window.order.update === 'function') {
				console.log('[GuestsManager] Found active order component, refreshing...');
				await window.order.update();
				console.log('[GuestsManager] Order component refresh completed');
			} else {
				console.log('[GuestsManager] No active order component to refresh');
			}
		} catch (error) {
			console.error('[GuestsManager] Error refreshing order component:', error);
		}
	}

	/**
	 * Обработчик скрытия попапа гостей
	 */
	_onPopupHidden() {
		console.log('GuestsManager: Popup hidden');
		// Только сбрасываем ссылку на активный компонент, так как он будет уничтожен автоматически
		// НЕ обновляем компоненты здесь - это уже делается в методах действий
		this.activeComponent = null;
	}

	/**
	 * Регистрирует базовую конфигурацию компонента в менеджере
	 */
	_registerComponent() {
		const baseConfig = {
			template: 'mod_crm/guests_manager',
			node: '.popup.guests-manager',
			attach: false, // Загружаем данные с сервера, но не рендерим DOM (используем готовую разметку)
			params: {
				id: this.invoiceId
			},
			// Автоматически генерируемые действия
			autoActions: {
				addTraveler: {
					successMessage: 'Гость успешно добавлен',
					errorMessage: 'Ошибка при добавлении гостя'
				},
				removeTraveler: {
					paramNames: ['id', 'invoiceId'], // Первый аргумент будет передан как 'id'
					successMessage: 'Гость успешно удален',
					errorMessage: 'Ошибка при удалении гостя'
				},
				changeTraveler: {
					paramNames: ['id', 'invoiceId', 'name', 'value'], // Аргументы по порядку
					successMessage: 'Данные гостя обновлены',
					errorMessage: 'Ошибка при обновлении данных гостя'
				},
				saveReference: {
					paramNames: ['invoiceId', 'value'], // Аргументы по порядку
					successMessage: 'Референс обновлен',
					errorMessage: 'Ошибка при обновлении референса'
				}
			},
			// Валидация данных
			validation: {
				addTraveler: {
					invoiceId: { required: true, type: 'number' },
					agecat: { required: true },
					age: { required: false, type: 'number' }
				},
				removeTraveler: {
					id: { required: true, type: 'number' },
					invoiceId: { required: true, type: 'number' }
				},
				changeTraveler: {
					id: { required: true, type: 'number' },
					invoiceId: { required: true, type: 'number' },
					name: { required: true, minLength: 1 },
					value: { required: true }
				},
				saveReference: {
					invoiceId: { required: true, type: 'number' },
					value: { required: true }
				}
			},
			// Обработка ошибок
			onError: (message, error) => {
				console.error('GuestsManager error:', message, error);
			},
			// Кастомная валидация
			customValidation: {
				addTraveler: (data) => {
					const errors = [];

					// Проверяем, что для детей указан возраст
					if (data.agecat === 'Child' && (!data.age || isNaN(Number(data.age)))) {
						errors.push('Для ребенка должен быть указан возраст');
					}

					// Проверяем, что возраст в допустимых пределах для детей
					if (data.agecat === 'Child' && data.age !== undefined) {
						const age = Number(data.age);
						if (age < 0 || age > 17) {
							errors.push('Возраст ребенка должен быть от 0 до 17 лет');
						}
					}

					return {
						isValid: errors.length === 0,
						errors
					};
				}
			},
			// События жизненного цикла
			events: VROCComponentFactory.createEvents({
				beforeInit: () => {
					console.log('GuestsManager: Initialization started');
				},
				afterInit: () => {
					console.log('GuestsManager: Initialization completed');
				},
				beforeAction: (data) => {
					console.log('GuestsManager: Action started', data.action);
				},
				afterAction: (data) => {
					console.log('GuestsManager: Action completed', data.action);
				},
				error: (data) => {
					console.error('GuestsManager: Error occurred', data.message);
				},
				shown: () => {
					console.log('GuestsManager: Popup shown');
				},
				hidden: this._onPopupHidden.bind(this)
			}),
			// Настройки уведомлений (можно заменить на любую систему)
			notifications: {
				show: (message, type) => {
					console.log(`[${type.toUpperCase()}] ${message}`);
					// Пример интеграции с toast:
					// if (window.toast) {
					//     window.toast[type](message);
					// }
				}
			}
		};

		// Регистрируем базовую конфигурацию в менеджере
		vrocManager.registerComponent('guestsManager', baseConfig);
	}

	/**
	 * Создает компонент для управления гостями
	 * @returns {Promise<VROCComponent>} - Созданный компонент
	 */
	async _createComponent() {
		// Уничтожаем предыдущий активный компонент если он есть
		if (this.activeComponent) {
			console.log('GuestsManager: Destroying previous component', this.activeComponent.id);
			// Правильно уничтожаем компонент через менеджер
			vrocManager.destroyComponent(this.activeComponent.id);
			this.activeComponent = null;
		}

		// Создаем новый компонент
		const component = await vrocManager.createComponent('guestsManager', {
			params: {
				id: this.invoiceId
				// Убираем action из базовых параметров, чтобы избежать дублирования
			}
		});

		this.activeComponent = component;
		console.log('GuestsManager: Component created');

		return component;
	}

	// Методы для управления попапом
	async show() {
		// Создаем компонент если его нет
		if (!this.activeComponent) {
			await this._createComponent();
		}

		if (this.activeComponent) {
			this.activeComponent.show();
		}
	}

	hide() {
		if (this.activeComponent) {
			this.activeComponent.hide();
		}
	}

	destroyActiveComponent() {
		if (this.activeComponent) {
			// Находим ID компонента в менеджере и уничтожаем его
			const components = vrocManager.getComponentsByName('guestsManager');
			const componentToDestroy = components.find(c => c === this.activeComponent);

			if (componentToDestroy) {
				// Получаем ID экземпляра из имени компонента
				const instanceId = componentToDestroy.id;
				vrocManager.destroyComponent(instanceId);
			}

			this.activeComponent = null;
			console.log('GuestsManager: Active component destroyed');
		}
	}

	destroyAllComponents() {
		vrocManager.destroyComponentsByName('guestsManager');
		this.activeComponent = null;
		console.log('GuestsManager: All components destroyed');
	}

	// Методы для работы с событиями компонента
	on(eventName, handler) {
		if (this.activeComponent) {
			this.activeComponent.on(eventName, handler);
		}
	}

	off(eventName, handler) {
		if (this.activeComponent) {
			this.activeComponent.off(eventName, handler);
		}
	}

	// Метод для обновления параметров
	async updateInvoiceId(newInvoiceId) {
		this.invoiceId = newInvoiceId;
		if (this.activeComponent) {
			await this.activeComponent.updateParams({
				id: newInvoiceId
				// Убираем action из параметров обновления
			});
		}
	}

	// Прокси-методы для доступа к автоматически сгенерированным методам компонента
	async addTraveler(element) {
		// Создаем компонент если его нет
		if (!this.activeComponent) {
			await this._createComponent();
		}

		// Определяем тип элемента и подготавливаем данные
		let data = { invoiceId: this.invoiceId };

		if (element && element.classList.contains('guests-manager-guests__add-kids-select')) {
			// Это селект для добавления ребенка
			const age = element.value;
			if (!age) return; // Если значение пустое, не добавляем

			data.agecat = 'Child';
			data.age = age;

			// Сбрасываем селект после добавления
			setTimeout(() => {
				element.value = '';
			}, 0);
		} else {
			// Это кнопка для добавления взрослого
			data.agecat = 'Adult';
		}

		try {
			console.log(`[GuestsManager] Executing addTraveler action with data:`, data);
			const result = await this.activeComponent?.addTraveler(data);

			// Обновляем компоненты сервисов и заказа после добавления гостя
			// (если действие не выбросило ошибку, значит оно успешное)
			// Добавляем защиту от повторных обновлений
			if (!this._isRefreshing) {
				this._isRefreshing = true;
				try {
					await this._refreshServiceComponents();
					await this._refreshOrderComponent();
				} finally {
					this._isRefreshing = false;
				}
			}

			console.log(`[GuestsManager] addTraveler completed successfully`);
			return result;
		} catch (error) {
			// Если действие выбросило ошибку, не обновляем компоненты сервисов
			console.error('[GuestsManager] Error in addTraveler, skipping service refresh:', error);
			throw error;
		}
	}

	async removeTraveler(id) {
		console.log(`[GuestsManager] removeTraveler called with id: ${id}`);

		// Создаем компонент если его нет
		if (!this.activeComponent) {
			await this._createComponent();
		}

		try {
			console.log(`[GuestsManager] Executing removeTraveler action for guest ${id}`);
			const result = await this.activeComponent?.removeTraveler(id, this.invoiceId);

			// Обновляем компоненты сервисов и заказа после удаления гостя
			// (если действие не выбросило ошибку, значит оно успешное)
			if (!this._isRefreshing) {
				this._isRefreshing = true;
				try {
					await this._refreshServiceComponents();
					await this._refreshOrderComponent();
				} finally {
					this._isRefreshing = false;
				}
			}

			console.log(`[GuestsManager] removeTraveler completed for guest ${id}`);
			return result;
		} catch (error) {
			// Если действие выбросило ошибку, не обновляем компоненты сервисов
			console.error('[GuestsManager] Error in removeTraveler, skipping service refresh:', error);
			throw error;
		}
	}

	async saveReference(element) {
		// Создаем компонент если его нет
		if (!this.activeComponent) {
			await this._createComponent();
		}

		try {
			const result = await this.activeComponent?.saveReference(this.invoiceId, element.value);
			
			if (!this._isRefreshing) {
				this._isRefreshing = true;
				try {
					await this._refreshServiceComponents();
					await this._refreshOrderComponent();
				} finally {
					this._isRefreshing = false;
				}
			}
			
			return result;
		} catch (error) {
			throw error;
		}
	}

	async changeTraveler(id, element) {
		// Создаем компонент если его нет
		if (!this.activeComponent) {
			await this._createComponent();
		}

		// Извлекаем данные из элемента
		const name = element.getAttribute('data-field-name') || element.name;
		const value = element.value;

		// Проверяем, что у нас есть все необходимые данные
		if (!id || !name) {
			console.warn('changeTraveler: missing required data', { id, name, value });
			return;
		}

		try {
			const result = await this.activeComponent?.changeTraveler(id, this.invoiceId, name, value);

			// Обновляем компоненты сервисов и заказа после изменения данных гостя
			// (если действие не выбросило ошибку, значит оно успешное)
			if (!this._isRefreshing) {
				this._isRefreshing = true;
				try {
					await this._refreshServiceComponents();
					await this._refreshOrderComponent();
				} finally {
					this._isRefreshing = false;
				}
			}

			return result;
		} catch (error) {
			// Если действие выбросило ошибку, не обновляем компоненты сервисов
			console.error('[GuestsManager] Error in changeTraveler, skipping service refresh:', error);
			throw error;
		}
	}

	/**
	 * Обновляет действия активного компонента
	 * @param {Object} newActions - Новые действия
	 */
	updateActiveComponentActions(newActions) {
		if (this.activeComponent) {
			vrocManager.updateComponentActions(this.activeComponent.id, newActions);
			console.log('GuestsManager: Updated component actions');
		}
	}

	/**
	 * Обновляет параметры активного компонента
	 * @param {Object} newParams - Новые параметры
	 * @param {boolean} reloadData - Перезагрузить данные
	 */
	async updateActiveComponentParams(newParams, reloadData = true) {
		if (this.activeComponent) {
			await vrocManager.updateComponentParams(this.activeComponent.id, newParams, reloadData);
			console.log('GuestsManager: Updated component params');
		}
	}

	/**
	 * Получает активный компонент
	 * @returns {VROCComponent|null}
	 */
	getActiveComponent() {
		return this.activeComponent;
	}
}

